import DesignForm from '@/components/forms/DesignForm/DesignForm'
import { RecordHistoryActionMenu } from '@/components/audit-log/RecordHistoryMenu'
import { AdminHeaderActionButton } from '@/components/patterns/admin-page'
import WebsiteDesignActivationAction from '@/components/websites/WebsiteDesignActivationAction'
import WebsiteDesignEditorHeader from '@/components/websites/WebsiteDesignEditorHeader'
import useWebsiteEditorFocusMode from '@/components/websites/useWebsiteEditorFocusMode'
import { usePathHelper } from '@/hooks/usePathHelper'
import { SeasonalReadinessNotice } from '@/components/seasonal/SeasonalReadinessNotice'
import { categoryQueries } from '@/lib/services/categories/categories.query'
import {
  collectionQueries,
  designCollectionsFilters,
} from '@/lib/services/collections/collections.query'
import {
  designQueries,
  useActivateDesignMutation,
  useDefaultDesign,
  useDesign,
} from '@/lib/services/designs/designs.query'
import { designSchema, getDesignOriginLabel } from '@/lib/services/designs/designs.schema'
import { designProductsFilters, productQueries } from '@/lib/services/products/products.query'
import { storeQueries, useStore } from '@/lib/services/stores/stores.query'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { getErrorMessage } from '@/lib/utils'
import { createFileRoute, Link, notFound, redirect, useNavigate } from '@tanstack/react-router'
import { CalendarDays, ChevronLeft, Maximize2, Minimize2, Wallpaper } from 'lucide-react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/$orgId/designs/$designId')({
  head: adminRouteTitleHead,
  params: {
    parse: (params) => designSchema.pick({ designId: true }).parse(params),
  },
  loader: async ({ context, params }) => {
    if (!context.ability.can('view', 'designs')) throw notFound()
    if (Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID))
      throw notFound()

    const design = await context.queryClient.ensureQueryData(
      designQueries.show({
        organizationId: params.orgId,
        designId: params.designId,
      })
    )

    if (design?.is_default) {
      throw redirect({
        to: '/$orgId/website/builder',
        params: { orgId: params.orgId },
        search: { section: 'brand-layout' },
        replace: true,
      })
    }

    await Promise.all([
      context.queryClient.ensureQueryData(designQueries.default({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(storeQueries.themes({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(
        collectionQueries.index({
          organizationId: params.orgId,
          filters: designCollectionsFilters,
        })
      ),
      context.queryClient.ensureQueryData(
        productQueries.index({
          organizationId: params.orgId,
          filters: designProductsFilters,
        })
      ),
      context.queryClient.ensureQueryData(categoryQueries.index({ organizationId: params.orgId })),
    ])

    return { design, isReadOnly: !context.ability.can('edit', 'designs') }
  },
  component: DesignShowPage,
})

export function DesignShowPage() {
  const params = Route.useParams()
  const navigate = useNavigate()
  const path = usePathHelper()
  const { data: design } = useDesign(params.designId)
  const { data: defaultDesign } = useDefaultDesign()
  const { data: store } = useStore()
  const { mutateAsync: activateDesign, isPending: isActivating } = useActivateDesignMutation()
  const { isFocusMode, toggleFocusMode } = useWebsiteEditorFocusMode()

  const activeScheduledDesignId = Number(store?.active_scheduled_design_id) || null
  const isActive =
    Number(store?.active_design_id) === Number(design?.id) ||
    Boolean(activeScheduledDesignId && activeScheduledDesignId === Number(design?.id))
  const scheduledDesignBlockingActivation =
    activeScheduledDesignId && activeScheduledDesignId !== Number(design?.id)
      ? {
          id: activeScheduledDesignId,
          name: store?.active_scheduled_design_name,
        }
      : null

  const handleActivateNow = async () => {
    try {
      await activateDesign(design?.id)
      toast.success('Design activated')
    } catch (error) {
      toast.error('Unable to activate design', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleOpenScheduledDesign = () => {
    if (!scheduledDesignBlockingActivation?.id) {
      return
    }

    void navigate({
      to: '/$orgId/designs/$designId',
      params: {
        orgId: params.orgId,
        designId: scheduledDesignBlockingActivation.id,
      },
    })
  }

  const nonDefaultDesignLabel = 'seasonal website update'
  const nonDefaultDesignBadge = 'Seasonal update'

  return (
    <div className="flex w-full max-w-none flex-col gap-y-4">
      <WebsiteDesignEditorHeader
        title={`Edit ${design?.name ?? nonDefaultDesignLabel}`}
        description="Customers see this update when it is live on its schedule."
        icon={Wallpaper}
        compact={isFocusMode}
        badges={[
          { label: nonDefaultDesignBadge, variant: 'brand', icon: CalendarDays },
          {
            label: getDesignOriginLabel(design),
            variant: design?.design_origin === 'platform' ? 'brand' : 'outline',
          },
        ]}
        actions={
          <>
            <AdminHeaderActionButton type="button" variant="outline" onClick={toggleFocusMode}>
              {isFocusMode ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
              {isFocusMode ? 'Exit Focus' : 'Focus'}
            </AdminHeaderActionButton>
            <AdminHeaderActionButton asChild variant="outline">
              <Link to="/$orgId/designs" params={{ orgId: params.orgId }}>
                <ChevronLeft className="size-4" />
                Back To Designs
              </Link>
            </AdminHeaderActionButton>

            <WebsiteDesignActivationAction
              activateTitle="Activate seasonal website update?"
              activateDescription={`This will make ${
                design?.name ?? 'this seasonal website update'
              } live on your storefront now. Your default design will still be available when this update is no longer live.`}
              isLive={isActive}
              isPending={isActivating}
              onActivate={handleActivateNow}
              onOpenScheduledDesign={handleOpenScheduledDesign}
              scheduledDesign={scheduledDesignBlockingActivation}
            />
            {design?.id ? (
              <RecordHistoryActionMenu
                link={path.designs.link('$designId/history', design.id)}
                triggerLabel="More website design actions"
              />
            ) : null}
          </>
        }
      />
      <DesignForm
        initialData={design}
        defaultDesign={defaultDesign}
        presentation="seasonal-workspace"
        seasonalNotice={
          <SeasonalReadinessNotice
            design={design}
            isEnabled={Boolean(design?.schedule?.is_enabled)}
          />
        }
      />
    </div>
  )
}
